import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabaseClient';
import { getPlanLimits, PlanLimits } from '../lib/planLimits';

interface SubscriptionContextType {
  subscription: any | null;
  isLoading: boolean;
  isOnTrial: boolean;
  isTrialExpired: boolean;
  isSubscribed: boolean;
  daysLeftInTrial: number | null;
  scheduledDeletionDate: Date | null;
  daysUntilDeletion: number | null;
  planLimits: PlanLimits;
  currentPlan: string;
  createCheckoutSession: (priceId: string) => Promise<string | null>;
  createPortalSession: () => Promise<string | null>;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, profile } = useAuth();
  const [subscription, setSubscription] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (user && profile) {
      fetchSubscription();
    } else {
      setSubscription(null);
      setIsLoading(false);
    }
  }, [user, profile]);

  const fetchSubscription = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    // Get subscription from database
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (!error && data && data.length > 0) {
      setSubscription(data[0]);
    } else {
      setSubscription(null);
    }
    
    setIsLoading(false);
  };

  // Calculate if user is on trial
  const isOnTrial = profile?.trial_end_date 
    ? new Date(profile.trial_end_date) > new Date() 
    : false;

  // Calculate if trial is expired
  const isTrialExpired = profile?.trial_end_date 
    ? new Date(profile.trial_end_date) <= new Date() 
    : false;

  // Calculate days left in trial
  const daysLeftInTrial = profile?.trial_end_date 
    ? Math.max(0, Math.ceil((new Date(profile.trial_end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) 
    : null;

  // Calculate scheduled deletion date and days until deletion
  const scheduledDeletionDate = profile?.scheduled_deletion_date 
    ? new Date(profile.scheduled_deletion_date) 
    : null;

  const daysUntilDeletion = scheduledDeletionDate 
    ? Math.max(0, Math.ceil((scheduledDeletionDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) 
    : null;

  // Check if user has an active subscription
  const isSubscribed = subscription?.status === 'active';

  // Get current plan
  const currentPlan = subscription?.plan || (isOnTrial ? 'trial' : 'starter');

  // Get plan limits based on current subscription status
  const planLimits = getPlanLimits(subscription, isOnTrial, isTrialExpired);

  // Create a checkout session for subscription
  const createCheckoutSession = async (priceId: string) => {
    if (!user) return null;

    try {const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId },
      });if (error) {// Try to get more detailed error information
        if (error.context && error.context.body) {}
        return null;
      }

      if (data && data.error) {return null;
      }

      if (!data?.url) {return null;
      }

      return data.url;
    } catch (error) {return null;
    }
  };

  // Create a portal session for subscription management
  const createPortalSession = async () => {
    if (!user) {
      throw new Error('User must be logged in to manage subscription');
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {});

      if (error) {throw new Error(error.message || 'Failed to create portal session');
      }

      if (!data) {throw new Error('No response from server');
      }

      if (data.error) {throw new Error(data.error);
      }

      if (!data.url) {throw new Error('No portal URL received from server');
      }

      return data.url;
    } catch (error) {throw error instanceof Error ? error : new Error('Failed to create portal session');
    }
  };

  const value = {
    subscription,
    isLoading,
    isOnTrial,
    isTrialExpired,
    isSubscribed,
    daysLeftInTrial,
    scheduledDeletionDate,
    daysUntilDeletion,
    planLimits,
    currentPlan,
    createCheckoutSession,
    createPortalSession,
    refreshSubscription: fetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};